import styled from '@emotion/styled'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'

export interface Props {
  cta?: ButtonProps
  image?: ImageProps
  index?: number
  subtitle?: string
  title?: string
}

export const Item = memo(function Item({
  image,
  index,
  subtitle,
  title,
  cta,
}: Props) {
  if (!image || !cta) {
    return null
  }

  return (
    <Container href={cta.URL}>
      {index ? (
        <Label className="item-label">{`0${index + 1}`}</Label>
      ) : (
        <Label className="item-label">01</Label>
      )}
      <Box className="item-box">
        <Image {...image} />
      </Box>
      <Bottom className="item-bottom" dial={7} space="between" row>
        <Text>
          {title ? <Title>{title}</Title> : null}
          {subtitle ? (
            <Subtitle className="item-label">{subtitle}</Subtitle>
          ) : null}
        </Text>
        <CTA noActions variant="white" className="item-cta" {...cta} />
      </Bottom>
    </Container>
  )
})

const Box = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;

  &:before {
    content: '';
    width: 100%;
    height: 50%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.8)}
    );
    backface-visibility: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
  }
  &:after {
    content: '';
    width: 100%;
    height: 100%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.4)}
    );
    backface-visibility: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    transition: 0.6s ease-in-out;
  }

  img {
    transition: 0.6s ease-in-out;
    ${styles.img}
  }

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const Label = styled.span`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  margin-bottom: 1.5rem;
  display: inline-flex;
  align-items: center;

  &:after {
    content: '';
    width: 7.5rem;
    height: 0.125rem;
    background: ${({ theme }) => theme.colors.variants.primaryDark};
    opacity: 0.2;
    margin-left: 1rem;
  }
`

const Bottom = styled(FlexBox)`
  padding: 2.25rem 0 0 0;
  z-index: 2;

  @media (max-width: 1199px) {
    padding: 1.5rem 0 0 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`

const Text = styled.div``

const Title = styled.h4`
  ${styles.title.normal}

  color: ${({ theme }) => theme.colors.variants.primaryDark};
`

const Subtitle = styled.span`
  ${styles.description.dark}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  margin-top: 0.375rem;
`

const CTA = styled(Button)`
  @media (min-width: 1200px) {
    position: absolute;
    right: 0;
  }
  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`

const Container = styled.a`
  display: inline-block;
  width: 100%;

  @media (min-width: 1200px) {
    &:hover {
      ${Box} {
        img {
          transform: scale(1.1);
        }
      }
    }
  }

  @media (max-width: 1199px) {
    width: 100%;
    margin-bottom: 0.75rem;
  }
`
